// The view state in the address: one query parameter per field that differs from
// the state's base, so links stay short and readable, for example
//   ?v=1&view=idle&throttle=0.25&camera=0.9,0.4,1.2,0,0.1,0,35
// The base is the named view the state names (or the defaults), so a link to a
// view is just ?v=1&view=<id>. See docs/contracts/view-state.md § The address.

import { FIELDS, getField, setField } from "./fields";
import { NAMED_VIEWS, findView, viewBase, type NamedView } from "./views";
import { jsonEqual, type ViewState } from "./view-state";

/** The version of the address format, written as v=1. */
export const URL_VERSION = 1;

const VIEW_FIELD = FIELDS.find((f) => f.key === "view")!;

/** Every parameter the view state owns; the app leaves any other parameter alone. */
export const VIEW_STATE_KEYS: ReadonlySet<string> = new Set(["v", ...FIELDS.map((f) => f.key)]);

// Keep the separators the codecs use (, ; :) readable; escape everything else that needs it.
function escape(text: string): string {
  return text.replace(/[^A-Za-z0-9\-._~,;:]/g, (c) => encodeURIComponent(c));
}

/** The query (without "?") that stands for `state`. */
export function encodeViewState(state: ViewState, views: readonly NamedView[] = NAMED_VIEWS): string {
  const base = viewBase(findView(state.view, views)).state;
  const parts = [`v=${URL_VERSION}`];
  if (state.view !== null) parts.push(`view=${escape(VIEW_FIELD.codec.encode(state.view))}`);
  for (const field of FIELDS) {
    if (field === VIEW_FIELD) continue;
    const value = getField(state, field);
    if (!jsonEqual(value, getField(base, field))) parts.push(`${field.key}=${escape(field.codec.encode(value))}`);
  }
  return parts.join("&");
}

export interface Decoded {
  state: ViewState;
  /** Parameters that were present but not valid, and were left at the base's value. */
  issues: string[];
}

/** Reads a view state from a query (with or without "?"). Never throws: bad values keep the base's. */
export function decodeViewState(search: string, views: readonly NamedView[] = NAMED_VIEWS): Decoded {
  const params = new URLSearchParams(search);
  const issues: string[] = [];
  const v = params.get("v");
  if (v !== null && v !== String(URL_VERSION)) issues.push(`v=${v}: unknown address version, read as ${URL_VERSION}`);

  let view: NamedView | undefined;
  const viewText = params.get("view");
  if (viewText !== null && viewText !== "-") {
    view = findView(viewText, views);
    if (view === undefined) issues.push(`view=${viewText}: no such named view`);
  }
  const base = viewBase(view);
  issues.push(...base.issues);
  const state = base.state;

  for (const field of FIELDS) {
    if (field === VIEW_FIELD) continue;
    const text = params.get(field.key);
    if (text === null) continue;
    const value = field.codec.decode(text);
    if (value === undefined) issues.push(`${field.key}=${text}: not a valid value`);
    else setField(state, field, value);
  }
  return { state, issues };
}
